import PropTypes from 'prop-types';
import React, { Component } from 'react';
import partial from 'lodash/partial';

import trackingService from '../../../../state/services/tracking';
import { isStringEqual, stringifyQS } from '../../../../utils';
import { getNumberOfResultsLabel } from '../../../../utils/formats';
import { Pagination } from '../../../../components/Pagination';
import EventRecommendRaceCardSearch from '../../../../components/EventRecommendRaceCardSearch';

import SearchResultCard from '../../../../shared/EventList/components/SearchResultCard';
import { withTracking } from '../../provider';

import {
  noResults,
  numberOfResults,
  eventList,
  backdrop,
  notification,
} from './styles.module.css';
import { SEGMENT_EVENTS } from '../../../../utils/tracking';

export class EventListContainer extends Component {
  componentDidMount() {
    if (this.props.events.length >= 0) {
      this.trackResults();
    }
  }

  componentDidUpdate(prevProps) {
    // Track events for any update of result set
    if (
      this.props.fetched &&
      !isStringEqual(
        this.props.events.map((e) => e.id),
        prevProps.events.map((e) => e.id),
      )
    ) {
      this.trackResults();
    }
  }

  getSearchPath = () => {
    const { query } = this.props;
    return `/s?${stringifyQS({ ...query, isMapActive: 'true' })}`;
  };

  trackResults = () => {
    const {
      currentPageNumber,
      resultsPerPage,
      events,
      query,
      source,
    } = this.props;

    const payload = {
      count_results: events.length,
      events: events.map((event, index) => {
        return {
          position: (currentPageNumber - 1) * resultsPerPage + index + 1,
          event_id: event.id,
          event_name: event.title,
        };
      }),
      ...(Object.keys(query).length && {
        filters: query,
      }),
      // Category pages have inconsistent source names, assume 'category' type if not explicitly 'search'
      page_type: source === 'search_results' ? 'search' : 'category',
    };
    trackingService.trackEvent(SEGMENT_EVENTS.SEARCH_RESULTS_VIEWED, payload);
  };

  trackButton = (name, params) => {
    const {
      // tracking from elsewhere (i.e. map view)
      handleExternalTracking,

      // tracking within categories
      injected: { tracking: { trackButtonClick } = {} } = {},
    } = this.props;

    if (handleExternalTracking) {
      return handleExternalTracking(name, params);
    }

    return trackButtonClick ? trackButtonClick(name, params) : null;
  };

  render() {
    const {
      className,
      error,
      events,
      showLoadMore,
      showRecommendRaceCard,
      hidePaginationLabel,
      total,
      currentPageNumber,
      resultsPerPage,
      showBackdrop,
      onPaginationChange,
      activeEventId,
      activeEventRef,
      onEventHoverOn,
      onEventHoverOff,
      injected: { tracking: { trackButtonClick } = {} } = {},
    } = this.props;

    if (!(events?.length > 0)) {
      if (error) {
        return (
          <div className={notification}>
            <p>Sorry, there was an error loading the events.</p>
          </div>
        );
      }

      return (
        <>
          <div>
            <div className={eventList}>
              {/* Desktop view */}
              <div className={noResults}>
                <p>No results</p>
                <EventRecommendRaceCardSearch isSearchPage />
              </div>

              {/* Mobile Map View */}
              <div className={notification}>
                <div>
                  <b>Hmm, looks like there are no events found.</b>
                </div>
                <div>To get more results try zooming out</div>
              </div>
            </div>
          </div>
        </>
      );
    }

    return (
      <div className={className} style={this.props.style}>
        {showBackdrop && <div className={backdrop} />}

        <div>
          <ul className={eventList}>
            {events.map((event, index) => {
              const isActiveCard = activeEventId === event.id;
              return (
                <SearchResultCard
                  onMouseEnter={onEventHoverOn}
                  onMouseLeave={onEventHoverOff}
                  elementRef={isActiveCard ? activeEventRef : undefined}
                  key={event.id}
                  active={isActiveCard}
                  event={event}
                  onClick={partial(this.trackButton, 'event', {
                    isActiveCard,
                    index,
                    anchorText: event.title,
                    eventId: event.id,
                  })}
                />
              );
            })}
            {showRecommendRaceCard &&
              currentPageNumber * resultsPerPage >= total && (
                <EventRecommendRaceCardSearch isSearchPage />
              )}
          </ul>
          {!hidePaginationLabel && (
            <div className={numberOfResults}>
              {getNumberOfResultsLabel(
                currentPageNumber,
                resultsPerPage,
                total,
              )}
            </div>
          )}
          {showLoadMore && (
            <Pagination
              {...this.props}
              smallTopPadding
              smallBottomPadding
              perPage={resultsPerPage}
              onClick={(newIndex) => {
                if (trackButtonClick) {
                  trackButtonClick('pagination', { newIndex });
                }
                onPaginationChange(newIndex);
              }}
              preventRedirect={false}
            />
          )}
        </div>
      </div>
    );
  }
}

EventListContainer.defaultProps = {
  className: '',
  error: null,
  onPaginationChange: () => {},
  query: undefined,
  showLoadMore: true,
  showRecommendRaceCard: true,
  source: undefined,
  total: 0,
};

EventListContainer.propTypes = {
  className: PropTypes.string,
  /**
   * Object representing current event list error state
   */
  error: PropTypes.shape(),
  /**
   * Array of event data objects currently loaded in state for this list
   */
  events: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  /**
   * A func to call when the page changes
   */
  onPaginationChange: PropTypes.func,
  /**
   * Object representing the current event list query to dispatch
   */
  query: PropTypes.shape(),
  /**
   * Boolean determining whether to show load more
   */
  showLoadMore: PropTypes.bool,
  /**
   * Boolean determining whether to show the recommend race card
   */
  showRecommendRaceCard: PropTypes.bool,
  /**
   * Optional string source location of list.
   */
  source: PropTypes.string,
  /**
   * Number of events for current list (and query) found on the server
   */
  total: PropTypes.number,
};

export default withTracking('event_list')(EventListContainer);
